// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform,
} from 'react-native';
import { Colors, FontWeight, Radii, Shadow } from '../../theme/tokens';
import { useApp } from '../../store/AppStore';
import { useResponsive } from '../../theme/responsive';
import { ScreenShell } from '../../components/WebShell';
import { Icon } from '../../components/Icon';
import PrimaryButton from '../../components/Button_Component/Primary_Button';
import CommonInput from '../../components/Common_Input/Common_Input';
import MobileNumberInput, { COUNTRIES } from '../../components/Mobile_Number/Mobile_Number';

// ─── Toggle ───────────────────────────────────────────────────────────────────

function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <TouchableOpacity
      onPress={onToggle}
      style={[styles.toggle, { backgroundColor: on ? Colors.accent : Colors.paper3, borderColor: on ? Colors.accent : Colors.lineStrong }]}
    >
      <View style={[styles.toggleThumb, { transform: [{ translateX: on ? 14 : 1 }] }]} />
    </TouchableOpacity>
  );
}

// ─── SectionH ─────────────────────────────────────────────────────────────────

function SectionH({ title, sub, icon, danger }: { title: string; sub?: string; icon?: string; danger?: boolean }) {
  return (
    <View style={styles.sectionH}>
      <View style={styles.sectionHRow}>
        {icon ? (
          <View style={[styles.sectionIcon, danger && styles.sectionIconDanger]}>
            <Icon name={icon as any} size={14} color={danger ? Colors.danger : Colors.accent} stroke={1.9} />
          </View>
        ) : null}
        <Text style={[styles.sectionTitle, danger && { color: Colors.danger }]}>{title}</Text>
      </View>
      {sub ? <Text style={styles.sectionSub}>{sub}</Text> : null}
    </View>
  );
}

// Web-only staggered fade-up entrance (react-native-web animationKeyframes).
function fadeUp(delayMs: number): any {
  if (Platform.OS !== 'web') return null;
  return {
    animationKeyframes: { '0%': { opacity: 0, transform: [{ translateY: 16 }] }, '100%': { opacity: 1, transform: [{ translateY: 0 }] } },
    animationDuration: '520ms',
    animationDelay: `${delayMs}ms`,
    animationFillMode: 'both',
    animationTimingFunction: 'cubic-bezier(.2,.7,.3,1)',
  };
}

function Card({ children, style }: { children: React.ReactNode; style?: any }) {
  return <View style={[styles.card, style]}>{children}</View>;
}

function CardRow({ children, last, style }: { children: React.ReactNode; last?: boolean; style?: any }) {
  return <View style={[styles.cardRow, !last && styles.cardRowBorder, style]}>{children}</View>;
}

// ─── GeneralSettingsScreen ────────────────────────────────────────────────────

export function GeneralSettingsScreen() {
  const { toast, orgSettings, saveOrgSettings } = useApp();
  const { isMobile, pagePadX } = useResponsive();
  // On mobile, two-up field grids stack into a single column.
  const gridStyle = (base: any) => [base, isMobile && styles.gridStack];

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [tz, setTz] = useState('Asia/Kolkata');
  const [locale, setLocale] = useState('en');
  const [businessHours, setBusinessHours] = useState(true);
  const [supportPhone, setSupportPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState(COUNTRIES.find(c => c.code === 'IN'));
  const [supportAddress, setSupportAddress] = useState('');

  // Sync from orgSettings when loaded
  useEffect(() => {
    if (orgSettings) {
      setName(orgSettings.brandName || '');
      setSlug(orgSettings.slug || '');
      setLocale(orgSettings.locale || 'en');
    }
  }, [orgSettings]);

  const handleSave = async () => {
    await saveOrgSettings({
      brandName: name,
      locale,
    });
  };

  return (
    <ScreenShell crumbs={[
      { label: 'Settings' },
      { label: 'General' },
    ]}>
      <View style={styles.root}>
        {/* Page Head */}
        <View style={[styles.pageHead, { paddingHorizontal: pagePadX }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pageTitle}>General</Text>
            <Text style={styles.pageSub}>Workspace name, locale, business hours, branding — everything that identifies your account.</Text>
          </View>
          <PrimaryButton
            title="Save changes"
            size="md"
            variant="primary"
            leftIcon={<Icon name="Check" size={14} color="#fff" />}
            onPress={handleSave}
          />
        </View>

        <ScrollView style={styles.body} contentContainerStyle={[styles.bodyContent, { padding: isMobile ? 16 : 24 }]}>
          {/* Workspace */}
          <SectionH icon="Settings" title="Workspace" sub="The name and slug appear in customer-facing emails and the portal." />
          <Card style={fadeUp(40)}>
            <CardRow>
              <View style={gridStyle(styles.grid2)}>
                <View style={styles.field}>
                  <CommonInput
                    label="Workspace name"
                    value={name}
                    onChangeText={setName}
                    placeholder="Acme Industries"
                    hint="Shown as the sender in customer emails."
                  />
                </View>
                <View style={styles.field}>
                  <CommonInput
                    label="Workspace slug"
                    leftIcon={<Text style={styles.slugPrefix}>softknack.io/</Text>}
                    value={slug}
                    onChangeText={v => setSlug(v.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="acme"
                    hint="Used in customer portal URLs. Lowercase letters and dashes only."
                  />
                </View>
              </View>
            </CardRow>
            <CardRow last>
              {/* Logo upload area */}
              <View style={styles.logoArea}>
                <View style={styles.logoPlaceholder}>
                  <Text style={styles.logoInitial}>{name.charAt(0).toUpperCase() || 'A'}</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.logoTitle}>Workspace logo</Text>
                  <Text style={styles.logoSub}>SVG, PNG, JPEG or WebP · max 2 MB · square aspect recommended. Drag a file here or click upload.</Text>
                </View>
                <PrimaryButton
                  title="Upload"
                  size="sm"
                  variant="outline"
                  leftIcon={<Icon name="Paper" size={12} color={Colors.ink} />}
                  onPress={() => toast('File picker · browser only.', 'info')}
                />
              </View>
            </CardRow>
          </Card>

          {/* Locale & Hours */}
          <SectionH icon="Clock" title="Locale & hours" sub="Affects timestamps, date formatting and outbound email rendering." />
          <Card style={fadeUp(110)}>
            <CardRow>
              <View style={gridStyle(styles.grid2)}>
                <View style={styles.field}>
                  <Text style={styles.fieldLabel}>Timezone</Text>
                  <View style={styles.selectFake}>
                    <Text style={styles.selectText}>{tz}</Text>
                    <Icon name="Chevron" size={12} color={Colors.ink3} />
                  </View>
                </View>
                <View style={styles.field}>
                  <Text style={styles.fieldLabel}>Locale</Text>
                  <View style={styles.selectFake}>
                    <Text style={styles.selectText}>{locale}</Text>
                    <Icon name="Chevron" size={12} color={Colors.ink3} />
                  </View>
                </View>
              </View>
            </CardRow>
            <CardRow last>
              <View style={styles.switchRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.switchTitle}>Honour business hours</Text>
                  <Text style={styles.switchSub}>SLA timers pause outside 09:00 – 18:00 IST, Mon–Fri.</Text>
                </View>
                <Toggle on={businessHours} onToggle={() => setBusinessHours(v => !v)} />
              </View>
            </CardRow>
          </Card>

          {/* Support phone & address */}
          <SectionH icon="Mail" title="Support phone & address" sub="Optional. Shown in your portal footer + automated emails." />
          <Card style={fadeUp(180)}>
            <CardRow last>
              <View style={gridStyle(styles.grid3)}>
                {/* Phone — compact, country-coded. Fixed width so it never
                    collapses (which made the label wrap vertically). */}
                <View style={{ flexGrow: 0, flexShrink: 0, width: isMobile ? '100%' : 340 }}>
                  <MobileNumberInput
                    label="PHONE"
                    selectedCountry={phoneCountry}
                    value={supportPhone}
                    onCountryChange={setPhoneCountry}
                    onChangeText={setSupportPhone}
                  />
                </View>
                {/* Address — fills the rest, single comfortable line */}
                <View style={{ flexGrow: 1, flexShrink: 1, minWidth: 0 }}>
                  <CommonInput
                    label="Address"
                    value={supportAddress}
                    onChangeText={setSupportAddress}
                    placeholder="Street, city, postal code"
                  />
                </View>
              </View>
            </CardRow>
          </Card>

          {/* Danger zone */}
          <SectionH icon="Alert" danger title="Danger zone" sub="Irreversible actions live here. Be careful." />
          <View style={[styles.dangerCard, fadeUp(250)]}>
            <View style={styles.dangerRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.dangerTitle}>Delete this workspace</Text>
                <Text style={styles.dangerSub}>All tickets, contacts, templates and integrations will be removed. This cannot be undone.</Text>
              </View>
              <PrimaryButton
                title="Delete workspace"
                size="md"
                variant="danger"
                leftIcon={<Icon name="X" size={13} color={Colors.white} />}
                onPress={() => toast('Delete workspace · requires the org owner to confirm in CLI.', 'warn', 'alert')}
              />
            </View>
          </View>
        </ScrollView>
      </View>
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  pageHead: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 16,
    paddingHorizontal: 28, paddingTop: 20, paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    backgroundColor: Colors.card, flexShrink: 0,
  },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageSub: { fontSize: 13, color: Colors.ink3, marginTop: 4 },
  toggle: {
    width: 32, height: 18, borderRadius: 999, borderWidth: 1,
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  toggleThumb: {
    width: 14, height: 14, borderRadius: 99,
    backgroundColor: Colors.white,
    shadowColor: '#101828', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.18, shadowRadius: 2,
  },
  body: { flex: 1 },
  bodyContent: { maxWidth: 880, alignSelf: 'center', width: '100%' as any, padding: 24, gap: 0 },
  sectionH: { marginBottom: 12, marginTop: 16 },
  sectionHRow: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  sectionIcon: {
    width: 28, height: 28, borderRadius: Radii.r2,
    backgroundColor: Colors.accentSoft, borderWidth: 1, borderColor: Colors.accentLine,
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  sectionIconDanger: { backgroundColor: Colors.dangerSoft, borderColor: Colors.dangerLine },
  sectionTitle: { fontSize: 15, fontWeight: FontWeight.bold, color: Colors.ink, letterSpacing: -0.2 },
  sectionSub: { fontSize: 12.5, color: Colors.ink3, marginTop: 4, marginLeft: 37 },
  card: {
    backgroundColor: Colors.card, borderRadius: Radii.r4,
    borderWidth: 1, borderColor: Colors.line,
    marginBottom: 16, overflow: 'hidden',
    ...(Shadow.base as object),
  },
  cardRow: { padding: 18 },
  cardRowBorder: { borderBottomWidth: 1, borderBottomColor: Colors.line },
  grid2: { flexDirection: 'row', gap: 20, alignItems: 'flex-start' },
  grid3: { flexDirection: 'row', gap: 20, alignItems: 'flex-start' },
  gridStack: { flexDirection: 'column', gap: 14 },
  field: { flex: 1 },
  fieldLabel: { fontSize: 13, fontWeight: FontWeight.medium, color: Colors.ink, marginBottom: 4 },
  slugPrefix: { fontSize: 13, color: Colors.ink3, fontWeight: FontWeight.medium },
  selectFake: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, paddingHorizontal: 10, paddingVertical: 8,
    backgroundColor: Colors.card,
  },
  selectText: { fontSize: 13.5, color: Colors.ink },
  logoArea: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  logoPlaceholder: {
    width: 64, height: 64, borderRadius: Radii.r4,
    backgroundColor: Colors.ink, justifyContent: 'center', alignItems: 'center',
    borderWidth: 1, borderColor: Colors.line, flexShrink: 0,
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(135deg, #312E81, #6366F1)', boxShadow: '0 8px 18px -8px rgba(79,70,229,0.55)' } as any
      : {}),
  },
  logoInitial: { fontSize: 24, fontWeight: FontWeight.bold, color: Colors.white },
  logoTitle: { fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.ink },
  logoSub: { fontSize: 12, color: Colors.ink3, marginTop: 2 },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: 16 },
  switchTitle: { fontSize: 13.5, fontWeight: FontWeight.medium, color: Colors.ink },
  switchSub: { fontSize: 12, color: Colors.ink3, marginTop: 2 },
  dangerCard: {
    backgroundColor: Colors.dangerSoft,
    borderRadius: Radii.r3,
    borderWidth: 1, borderColor: Colors.dangerLine,
    marginBottom: 16, overflow: 'hidden',
  },
  dangerRow: {
    padding: 16, flexDirection: 'row',
    alignItems: 'center', gap: 16,
  },
  dangerTitle: { fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.danger },
  dangerSub: { fontSize: 12.5, color: Colors.ink3, marginTop: 2 },
});
